/**
 * Translate a LabelDto to the appropriate language
 *
 * @param {Object} label - The LabelDto object with text and labelTranslations
 * @param {string} language - The current language code (e.g., 'en', 'fr')
 * @returns {string} The translated text or fallback to default text
 *
 * @example
 * const label = {
 *   text: "Default English Text",
 *   labelTranslations: [
 *     { language: { cultureString: "En" }, text: "English Text" },
 *     { language: { cultureString: "Fr" }, text: "Texte Français" }
 *   ]
 * };
 *
 * translateLabel(label, 'fr') // Returns: "Texte Français"
 * translateLabel(label, 'en') // Returns: "English Text"
 * translateLabel(label, 'es') // Returns: "Default English Text" (fallback)
 */
export const translateLabel = (label, language) => {
  // Return empty string if label is null/undefined
  if (!label) return '';

  // If no translations available, return the default text
  if (!label.labelTranslations || label.labelTranslations.length === 0) {
    return label.text || '';
  }

  // Normalize the language code to match against cultureString
  const normalizedLanguage = language?.toLowerCase() || 'en';

  // Find translation that matches the current language
  const translation = label.labelTranslations.find(lt => lt.language?.cultureString?.toLowerCase() === normalizedLanguage);

  // Return the translated text if found, otherwise fallback to default text
  return translation?.text || label.text || '';
};

/**
 * React hook-friendly version that uses i18n directly
 *
 * @param {Object} label - The LabelDto object
 * @param {Object} i18n - The i18n instance from useTranslation()
 * @returns {string} The translated text
 *
 * @example
 * import { useTranslation } from 'react-i18next';
 * import { useLabelTranslation } from 'services/utils/labelUtils';
 *
 * const MyComponent = ({ criteriaOption }) => {
 *   const { i18n } = useTranslation();
 *   const name = useLabelTranslation(criteriaOption.name, i18n);
 *   const description = useLabelTranslation(criteriaOption.description, i18n);
 *
 *   return <div>{name}: {description}</div>;
 * };
 */
export const useLabelTranslation = (label, i18n) => {
  return translateLabel(label, i18n.language);
};

/**
 * Batch translate multiple labels at once
 *
 * @param {Array<Object>} labels - Array of LabelDto objects
 * @param {string} language - The current language code
 * @returns {Array<string>} Array of translated texts
 *
 * @example
 * const labels = [label1, label2, label3];
 * const translated = translateLabels(labels, 'fr');
 */
export const translateLabels = (labels, language) => {
  if (!Array.isArray(labels)) return [];
  return labels.map(label => translateLabel(label, language));
};

/**
 * Get translation or return a default value
 *
 * @param {Object} label - The LabelDto object
 * @param {string} language - The current language code
 * @param {string} defaultValue - Default value if translation not found
 * @returns {string} The translated text or default value
 *
 * @example
 * translateLabelWithDefault(label, 'fr', 'N/A')
 */
export const translateLabelWithDefault = (label, language, defaultValue = '') => {
  const translated = translateLabel(label, language);
  return translated || defaultValue;
};
